import { useCallback, useContext, useEffect, useState } from "react"

import { ApplicationContext } from 'context'

import OnBoardingContainer from "components/OnBoardingContainer"

import OnBoardingDashboard from "components/OnBoardingDashboard"
import OnBoardingInformation from "components/OnBoardingInformation"
import OnBoardingUploads from "components/OnBoardingUploads"
import OnBoardingSign from "components/OnBoardingSign"

import { discectError } from "lib/utils"
import api from 'lib/api'

// import styles from "./styles.module.scss"

const DEFAULT_REQUIREMENTS = {
  sections: [],
  uploads: [],
  files: [],
  isInformationComplete: false,
  isFilesSigned: false,
  isValid: false
}

const Onboarding = (props) => {
  const { contextAgency, contextError, contextLoading, useCurrentPath } = useContext(ApplicationContext)
  const [isLoading, setIsLoading] = contextLoading // eslint-disable-line
  const [_, setError] = contextError // eslint-disable-line
  const [agency] = contextAgency // eslint-disable-line

  const [tab, setTab] = useState('DASHBOARD')
  const [onboardingToken, setOnboardingToken] = useState(null)
  const [requirements, setRequirements] = useState(DEFAULT_REQUIREMENTS)

  const currentPath = useCurrentPath()

  let surety = null
  if (agency?.sureties?.length > 0) {
    surety = agency.sureties[0]
  }

  const _getJwt = useCallback(async (token, force = false) => {
    let tokenResults = null
    const answerToken = localStorage.getItem('answerToken')
    if (!answerToken || force) {
      tokenResults = await api.warehouse.buildAnswerGroupToken(token)
      if (tokenResults) {
        localStorage.setItem('answerToken', JSON.stringify(tokenResults))
      } else {
        throw new Error('Token is invalid')
      }
    } else {
      tokenResults = JSON.parse(answerToken)  
    }
    return tokenResults
  }, [])

  const _fetchAgencyInfo = useCallback(async (areas = null) => {
    const { settings: { uploads: suretyUploads } } = surety
    const { employeeToken: token, requiredUploads } = agency.owner
    setIsLoading(true)
    try {
      const tokenResults = await _getJwt(token)
      if (tokenResults) {
        
        let newInfo = {}

        let incomingFiles = []
        if (!areas || areas.includes('UPLOADS')) {
          const { files } = await api.files.fetchSecureUrls(token, {})
          if (files) {
            incomingFiles = [...files]
          }

          // Uploads & Status
          if (requiredUploads) {
            newInfo.isUploadsComplete = true
            newInfo.uploads = requiredUploads.map(itm => {
              const upload = suretyUploads.find(u => u.id === itm)
              if (upload) {

                let isComplete = true
                let fileType = upload.name.replace(/ /g, '_').toUpperCase()
                if (upload.isDoubleSided) {
                  const frontFile = incomingFiles.find(itm => (itm.metadata.uploadid === upload.id && itm.metadata.type === `${fileType}_FRONT`))
                  const backFile = incomingFiles.find(itm => (itm.metadata.uploadid === upload.id && itm.metadata.type === `${fileType}_BACK`))
                  if (!frontFile || !backFile) {
                    isComplete = false
                    newInfo.isUploadsComplete = false
                  }
                }

                return { ...upload, isComplete }
              }
              return null
            }).filter(itm => !!itm)
          }
        }

        // Information & Status
        if (!areas || areas.includes('INFORMATION')) {
          const { sections: userDocs } = await api.warehouse.fetchAnswerGroup(token, tokenResults.token)
          if (userDocs) {
            newInfo.sections = [...userDocs]
          }
          const { isCompleted } = await api.warehouse.fetchNextQuestion(token, tokenResults.token)
          if (isCompleted) {
            newInfo.isInformationComplete = true
          }
        }
        
        // Form Requests & Status
        if (!areas || areas.includes('REQUESTS')) {
          const { requests: userRequests } = await api.warehouse.fetchAnswerGroupRequests(token, tokenResults.token)
          if (userRequests) {

            const fileIds = userRequests.map(itm => itm.fileId)
            const { fileDetails } = await api.documents.fetchDocumentDetails(token, { files: fileIds })

            newInfo.fileDetails = fileDetails || []

            newInfo.files = [...userRequests]
            let isFilesSigned = true
            let isContractsSigned = true
            userRequests.forEach(itm => {
              let isFound = fileDetails.find(f => f.id === itm.fileId)
              if (!itm.isComplete && !isFound.isContract) isFilesSigned = false 
              if (!itm.isComplete && !!isFound.isContract) isContractsSigned = false
            })
            newInfo.isFilesSigned = isFilesSigned
            newInfo.isContractsSigned = isContractsSigned
          }
        }

        if (agency.decision) {
          newInfo.isApplicationReviewed = true
        } else {
          newInfo.isApplicationReviewed = false
        }

        setRequirements(i => { return { ...i, ...newInfo, isValid: true } })
        setOnboardingToken(token)
        
      } else {
        throw new Error('Token is invalid')
      }

    } catch(e) {
      const error = discectError(e)
      console.log(e)
      console.log(error)
      if (['jwt expired','invalid signature','hash is invalid'].indexOf(error.toLowerCase()) >= 0) {
        await _getJwt(token, true)
        window.location.reload()
      }
      setError(error)
    }
    setIsLoading(false)
  }, [agency, surety, setError, setIsLoading, _getJwt])

  // Fetch
  useEffect(() => {
    if (agency) _fetchAgencyInfo()
  }, [surety, agency, _fetchAgencyInfo])

  useEffect(()=> {
    if(currentPath === '/onboarding') {
      setTab('DASHBOARD')
    } else if (currentPath === '/onboarding/information') {
      setTab('INFORMATION')
    } else if (currentPath === '/onboarding/uploads') {
      setTab('UPLOADS')
    } else if (currentPath === '/onboarding/sign' || currentPath === '/onboarding/sign/contract') {
      setTab('SIGN')
    }
  }, [currentPath])

  if (agency) {
    return (
      <OnBoardingContainer>
        {tab === 'DASHBOARD' && <OnBoardingDashboard surety={surety} agency={agency} requirements={requirements} onRefresh={_fetchAgencyInfo} />}
        {tab === 'INFORMATION' && <OnBoardingInformation surety={surety} agency={agency} token={onboardingToken} requirements={requirements} onGetToken={_getJwt} onRefresh={_fetchAgencyInfo} />}
        {tab === 'UPLOADS' && <OnBoardingUploads surety={surety} agency={agency} token={onboardingToken} requirements={requirements} onRefresh={_fetchAgencyInfo} />}
        {tab === 'SIGN' && <OnBoardingSign surety={surety} agency={agency} token={onboardingToken} requirements={requirements} onGetToken={_getJwt} onRefresh={_fetchAgencyInfo} />}
      </OnBoardingContainer>
    )
  }
  return null
}

export default Onboarding
